import {
  BillingDetails,
  PricingConfiguration,
} from '../../components/Billing/types';
import { IBillingConfig } from '../../schemas/apis/interface';

/**
 * Parse billing increment string to seconds
 * @param increment - String like "6 seconds" or "60 seconds"
 * @returns Number of seconds (6, 30, or 60)
 */
export const parseBillingIncrement = (increment: string): 6 | 30 | 60 => {
  const match = /(\d+)/.exec(increment);
  if (match) {
    const value = parseInt(match[1], 10);
    return value === 6 || value === 30 || value === 60 ? value : 60;
  }
  return 60;
};

/**
 * Format seconds to billing increment string
 * @param seconds - Number of seconds (6, 30, or 60)
 * @returns String like "6 seconds" or "60 seconds"
 */
export const formatBillingIncrement = (seconds: number): string => {
  return `${seconds} seconds`;
};

/**
 * Transform UI format (BillingDetails + PricingConfiguration) to API format
 * @param billingDetails - Billing details from UI
 * @param pricingConfig - Pricing configuration from UI
 * @returns API-compatible billing config
 */
export const transformToApiFormat = (
  billingDetails: BillingDetails,
  pricingConfig: PricingConfiguration
): IBillingConfig => {
  return {
    companyDetails: {
      companyName: billingDetails.companyName,
      email: billingDetails.email,
      phone: billingDetails.phone || undefined,
      taxId: billingDetails.gstNumber || undefined,
    },
    address: {
      streetAddress1: billingDetails.address.street1,
      streetAddress2: billingDetails.address.street2 || undefined,
      city: billingDetails.address.city,
      state: billingDetails.address.state,
      zipCode: billingDetails.address.zipCode,
      country: billingDetails.address.country,
    },
    gst: {
      enabled: pricingConfig.gstEnabled,
      gstin: pricingConfig.gstNumber || undefined,
      percentage: pricingConfig.gstPercentage,
    },
    callCharges: {
      perMinuteRate: pricingConfig.callRate,
      currency: pricingConfig.currency as 'INR' | 'USD' | 'EUR' | 'GBP',
      billingIncrement: parseBillingIncrement(pricingConfig.billingIncrement),
    },
    telephony: {
      billingMode: pricingConfig.telephonyMode,
      bundledRate:
        pricingConfig.telephonyMode === 'bundled'
          ? pricingConfig.telephonyRate
          : undefined,
      separateBilling:
        pricingConfig.telephonyMode === 'separate'
          ? {
              monthlyFee: 0,
              setupFee: 0,
              perMinuteRate: pricingConfig.telephonyRate,
            }
          : undefined,
    },
    callForwarding: {
      enabled: pricingConfig.callForwardingEnabled,
      perMinuteRate: pricingConfig.callForwardingEnabled
        ? pricingConfig.forwardingRate
        : undefined,
      description: pricingConfig.callForwardingEnabled
        ? pricingConfig.forwardingDescription
        : undefined,
    },
  };
};

/**
 * Transform API format to UI format (BillingDetails + PricingConfiguration)
 * @param apiConfig - API billing config
 * @returns Object containing billingDetails and pricingConfig
 */
export const transformFromApiFormat = (
  apiConfig: IBillingConfig
): {
  billingDetails: BillingDetails;
  pricingConfig: PricingConfiguration;
} => {
  const billingDetails: BillingDetails = {
    companyName: apiConfig.companyDetails.companyName,
    email: apiConfig.companyDetails.email,
    phone: apiConfig.companyDetails.phone || '',
    gstNumber: apiConfig.companyDetails.taxId || '',
    address: {
      street1: apiConfig.address.streetAddress1,
      street2: apiConfig.address.streetAddress2 || '',
      city: apiConfig.address.city,
      state: apiConfig.address.state,
      zipCode: apiConfig.address.zipCode,
      country: apiConfig.address.country,
    },
  };

  const pricingConfig: PricingConfiguration = {
    gstEnabled: apiConfig.gst?.enabled ?? true,
    gstNumber: apiConfig.gst?.gstin || '',
    gstPercentage: apiConfig.gst?.percentage ?? 18,
    callRate: apiConfig.callCharges?.perMinuteRate ?? 0.05,
    currency: apiConfig.callCharges?.currency ?? 'USD',
    billingIncrement: formatBillingIncrement(
      apiConfig.callCharges?.billingIncrement ?? 60
    ),
    telephonyMode: apiConfig.telephony?.billingMode ?? 'bundled',
    telephonyRate:
      apiConfig.telephony?.billingMode === 'bundled'
        ? (apiConfig.telephony.bundledRate ?? 0.02)
        : (apiConfig.telephony?.separateBilling?.perMinuteRate ?? 0.02),
    callForwardingEnabled: apiConfig.callForwarding?.enabled ?? false,
    forwardingRate: apiConfig.callForwarding?.perMinuteRate ?? 0.03,
    forwardingDescription:
      apiConfig.callForwarding?.description ||
      'Additional charges for forwarding calls to external numbers',
  };

  return { billingDetails, pricingConfig };
};

/**
 * Get default billing details
 */
export const getDefaultBillingDetails = (): BillingDetails => ({
  companyName: '',
  email: '',
  phone: '',
  gstNumber: '',
  address: {
    street1: '',
    street2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'India',
  },
});

/**
 * Get default pricing configuration
 */
export const getDefaultPricingConfig = (): PricingConfiguration => ({
  gstEnabled: true,
  gstNumber: '',
  gstPercentage: 18,
  callRate: 0.05,
  currency: 'USD',
  billingIncrement: '60 seconds',
  telephonyMode: 'bundled',
  telephonyRate: 0.02,
  callForwardingEnabled: false,
  forwardingRate: 0.03,
  forwardingDescription:
    'Additional charges for forwarding calls to external numbers',
});
